import { useEffect } from "react";
import AppCache from "../Helper/Common/AppCache";
import useGlobalFilterData from "./useGlobalFilterData";

/**
 * useCustomDashboardGlobalFilter
 *
 * Wraps useGlobalFilterData and handles the fleet + location initialization
 * effects shared between CustomOverviewMain and CustomInventoryPage.
 *
 * Reads globalFilterConfig from customDashboards[activeKey].globalFilter so
 * the custom platform only uses its own filter config, not the root org one.
 */
const useCustomDashboardGlobalFilter = () => {
    const customObsConfig = AppCache.getItem("customObservabilityConfig") || {};
    const globalFilterConfig =
        customObsConfig.globalFilter?.find((f) => f.type === "unifiedGlobalFilter") || null;

    const {
        allFleets,
        selectedFleets,
        allOnboardingLocations,
        allOnboardingLocationsHierarchy,
        globalFilters,
        setGlobalFilters,
        fetchFleets,
        fetchOnboardingLocations,
        handleFleetAndLocationChange,
        canUseGlobalAdvancedFilter,
        fleetsLoaded,
    } = useGlobalFilterData();

    // fetchFleets is a plain function from useGlobalFilterData (not wrapped in
    // useCallback), so its reference changes each render. The intent here is
    // mount-only: run once to seed fleet data after login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchFleets();
    }, []);

    // fetchOnboardingLocations has the same stability caveat as fetchFleets above.
    // The deps that actually control when this should re-run are the data values
    // (fleetsLoaded, allFleets, selectedFleets), which are listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!fleetsLoaded) return;
        const defaultOperationalStatus = ["operational", "non_operational"];
        if (allFleets.length === 0) {
            setGlobalFilters(prev => ({
                ...prev,
                operationalStatus: prev.operationalStatus || defaultOperationalStatus,
                allOperationalStatus: defaultOperationalStatus,
            }));
            return;
        }
        if (selectedFleets.length > 0) {
            fetchOnboardingLocations(selectedFleets, {
                operationalStatus: defaultOperationalStatus,
                allOperationalStatus: defaultOperationalStatus,
            });
        }
    }, [fleetsLoaded, allFleets, selectedFleets]);

    const handleDepotSelection = (selectedOptions) => {
        setGlobalFilters(prev => ({
            ...prev,
            onboardingLocations: { ...prev.onboardingLocations, depots: selectedOptions },
        }));
    };

    return {
        globalFilters,
        setGlobalFilters,
        globalFilterConfig,
        allFleets,
        allOnboardingLocations,
        allOnboardingLocationsHierarchy,
        canUseGlobalAdvancedFilter,
        handleFleetAndLocationChange,
        handleDepotSelection,
    };
};

export default useCustomDashboardGlobalFilter;
